import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Editor for a tenant's form layout (FormLayout, FormLayoutsController). Nothing is sent until the
// form is submitted: `serialize` then writes the whole arrangement into `payload` as JSON.
//
// Fields are dragged with SortableJS between the section lists and the "hidden" list. Those lists
// sit side by side, never inside one another. A locked field (one a record cannot be saved
// without, or a required custom field) is refused by the hidden list; the model rejects it too.
//
// Sections are NOT a SortableJS list. A section contains a field list, and SortableJS could not
// drag an element that contains another sortable: the drop never completed and the section stayed
// in its faded ghost state (tried native and fallback mode, dragoverBubble, disabling and hiding
// the inner lists). SortableJS keeps its drag state in module globals shared by every instance, so
// it is not fixable from the outside. A section is moved by its handle instead: the pointer is
// captured on the section list, so the release always arrives, and the section is re-inserted
// wherever the pointer crosses a neighbour's middle. Arrow up/down on the focused handle does the
// same. The capture sits on the list, not on the handle: re-inserting the section takes the handle
// out of the document for a moment, which cancels a capture held by the handle after one step.
//
// A built-in field's pencil opens one input per label it has (FormLayout#field_labels). They sit
// inside the draggable row, so SortableJS is told to leave their panel alone (`filter`); a blank one means
// the standard name and is left out of the payload.
//
// A built-in field's "Pflichtfeld" checkboxes (FormLayout#required_fields) sit in the row as well. A
// field with a ticked one counts as locked: the hidden list refuses it, and ticking one on a hidden
// field moves it into the first section.
export default class extends Controller {
  static targets = [
    "payload",
    "sections",
    "section",
    "list",
    "title",
    "sectionTemplate",
    "removeButton",
  ]

  static EDGE = 60
  static SCROLL_STEP = 14
  static DRAGGING_CLASSES = ["relative", "z-10", "shadow-lg", "ring-2", "ring-green-500"]

  connect() {
    this.fieldSortables ||= new Map()
    this.refresh()
  }

  disconnect() {
    this.endSectionDrag?.()
    for (const sortable of this.fieldSortables.values()) sortable.destroy()
    this.fieldSortables.clear()
  }

  // Fires for the lists rendered with the page and for every section added later.
  listTargetConnected(list) {
    this.fieldSortables ||= new Map()
    this.fieldSortables.set(
      list,
      Sortable.create(list, {
        group: { name: "form-layout-fields", put: (_to, _from, item) => this.accepts(list, item) },
        draggable: "[data-key]",
        animation: 150,
        // Pointer events instead of native HTML5 drag and drop: the same behaviour in every
        // browser, and a click on a field is not a drag.
        forceFallback: true,
        fallbackOnBody: true,
        fallbackTolerance: 3,
        // Let go outside every list: the field goes back to where it came from.
        revertOnSpill: true,
        // Typing, selecting text and the pencil inside a row are not a drag, nor is anything
        // else in the rename panel (its captions and hint).
        filter: "[data-rename-panel], [data-rename-toggle], [data-required-panel]",
        preventOnFilter: false,
        onSort: () => this.refresh(),
      }),
    )
  }

  listTargetDisconnected(list) {
    this.fieldSortables?.get(list)?.destroy()
    this.fieldSortables?.delete(list)
  }

  // --- Sections ----------------------------------------------------------------------------------

  startSectionDrag(event) {
    if (event.button !== 0) return
    event.preventDefault()

    const section = this.sectionOf(event.currentTarget)
    const surface = this.sectionsTarget
    const scroller = this.scrollParentOf(surface)
    surface.setPointerCapture?.(event.pointerId)
    section.classList.add(...this.constructor.DRAGGING_CLASSES)

    const move = (moveEvent) => {
      this.autoScroll(scroller, moveEvent.clientY)
      this.placeSection(section, moveEvent.clientY)
    }
    const events = ["pointerup", "pointercancel", "lostpointercapture"]
    this.endSectionDrag = () => {
      surface.removeEventListener("pointermove", move)
      for (const name of events) surface.removeEventListener(name, this.endSectionDrag)
      if (surface.hasPointerCapture?.(event.pointerId))
        surface.releasePointerCapture(event.pointerId)
      section.classList.remove(...this.constructor.DRAGGING_CLASSES)
      this.endSectionDrag = null
      this.refresh()
    }
    surface.addEventListener("pointermove", move)
    for (const name of events) surface.addEventListener(name, this.endSectionDrag)
  }

  moveSectionWithKeyboard(event) {
    const section = this.sectionOf(event.currentTarget)
    if (event.key === "ArrowUp" && section.previousElementSibling) {
      section.previousElementSibling.before(section)
    } else if (event.key === "ArrowDown" && section.nextElementSibling) {
      section.nextElementSibling.after(section)
    } else {
      return
    }
    event.preventDefault()
    event.currentTarget.focus()
    this.refresh()
  }

  // Puts the dragged section before the first other section whose middle lies below the pointer,
  // or last. Measured against the others only, so a tall dragged section does not skew the answer.
  placeSection(section, clientY) {
    const others = this.sectionTargets.filter((candidate) => candidate !== section)
    const before = others.find((candidate) => {
      const rect = candidate.getBoundingClientRect()
      return clientY < rect.top + rect.height / 2
    })

    if (before) {
      if (section.nextElementSibling !== before) this.sectionsTarget.insertBefore(section, before)
    } else if (this.sectionsTarget.lastElementChild !== section) {
      this.sectionsTarget.append(section)
    }
  }

  autoScroll(scroller, clientY) {
    const { EDGE, SCROLL_STEP } = this.constructor
    const bounds =
      scroller === document.scrollingElement
        ? { top: 0, bottom: window.innerHeight }
        : scroller.getBoundingClientRect()
    if (clientY < bounds.top + EDGE) scroller.scrollTop -= SCROLL_STEP
    else if (clientY > bounds.bottom - EDGE) scroller.scrollTop += SCROLL_STEP
  }

  scrollParentOf(element) {
    for (let node = element.parentElement; node; node = node.parentElement) {
      const { overflowY } = getComputedStyle(node)
      if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node
    }
    return document.scrollingElement
  }

  addSection() {
    const section = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true)
    section.dataset.sectionId = this.newSectionId()
    this.sectionsTarget.append(section)
    this.refresh()

    const title = section.querySelector("[data-form-layout-editor-target~='title']")
    title.focus()
    title.select()
  }

  // The section's fields are not lost with it: optional ones go to the hidden list, locked ones
  // to the first remaining section. The last section cannot be removed.
  removeSection(event) {
    if (this.sectionTargets.length <= 1) return

    const section = this.sectionOf(event.target)
    const remaining = this.sectionTargets.filter((candidate) => candidate !== section)
    const firstList = this.listOf(remaining[0])
    for (const item of this.itemsOf(this.listOf(section))) {
      ;(this.isLocked(item) ? firstList : this.hiddenList).append(item)
    }
    section.remove()
    this.refresh()
  }

  // --- Field labels ------------------------------------------------------------------------------

  toggleRename(event) {
    const toggle = event.currentTarget
    const panel = toggle.closest("[data-key]").querySelector("[data-rename-panel]")
    const opening = panel.hidden
    panel.hidden = !opening
    toggle.setAttribute("aria-expanded", String(opening))
    if (opening) panel.querySelector("[data-label-key]")?.focus()
  }

  // The chip next to the field's name lists its own labels, as typed so far.
  updateRenamed(event) {
    const item = event.target.closest("[data-key]")
    const summary = item.querySelector("[data-renamed-summary]")
    const labels = Array.from(item.querySelectorAll("[data-label-key]"))
      .map((input) => input.value.trim())
      .filter(Boolean)
    summary.textContent = labels.join(" · ")
    summary.hidden = labels.length === 0
  }

  // --- Required fields ---------------------------------------------------------------------------

  toggleRequired(event) {
    const item = event.target.closest("[data-key]")
    if (!event.target.checked || !this.isHiddenList(item.parentElement)) return

    this.listOf(this.sectionTargets[0]).append(item)
    this.refresh()
  }

  // --- Submit ------------------------------------------------------------------------------------

  serialize() {
    const sections = this.sectionTargets.map((section) => {
      const title = section.querySelector("[data-form-layout-editor-target~='title']").value.trim()
      const { sectionId, titleKey, hintKey, originalTitle } = section.dataset
      // An untouched default section keeps its translation key; a renamed one stores the text.
      const heading =
        titleKey && title === originalTitle
          ? { title_key: titleKey, hint_key: hintKey || null }
          : { title }
      return { id: sectionId, ...heading, fields: this.keysOf(this.listOf(section)) }
    })

    this.payloadTarget.value = JSON.stringify({
      sections,
      hidden_fields: this.keysOf(this.hiddenList),
      field_labels: this.fieldLabels(),
      required_fields: this.requiredFields(),
    })
  }

  refresh() {
    for (const list of this.listTargets) {
      list.nextElementSibling?.toggleAttribute("hidden", this.itemsOf(list).length > 0)
    }
    const single = this.sectionTargets.length <= 1
    for (const button of this.removeButtonTargets) button.disabled = single
  }

  fieldLabels() {
    const labels = {}
    for (const input of this.element.querySelectorAll("[data-label-key]")) {
      const label = input.value.trim()
      if (label) labels[input.dataset.labelKey] = label
    }
    return labels
  }

  requiredFields() {
    return Array.from(this.element.querySelectorAll("[data-required-key]:checked")).map(
      (input) => input.dataset.requiredKey,
    )
  }

  // --- Helpers -----------------------------------------------------------------------------------

  // Whether a field list takes the dragged element: fields only, and no locked ones when hidden.
  accepts(list, item) {
    if (!item.dataset.key) return false

    return !(this.isHiddenList(list) && this.isLocked(item))
  }

  get hiddenList() {
    return this.listTargets.find((list) => this.isHiddenList(list))
  }

  sectionOf(element) {
    return element.closest("[data-form-layout-editor-target~='section']")
  }

  listOf(section) {
    return section.querySelector("[data-form-layout-editor-target~='list']")
  }

  itemsOf(list) {
    return Array.from(list.children).filter((child) => child.dataset.key)
  }

  keysOf(list) {
    return this.itemsOf(list).map((item) => item.dataset.key)
  }

  isHiddenList(list) {
    return list.dataset.hiddenList === "true"
  }

  isLocked(item) {
    return (
      item.dataset.locked === "true" || item.querySelector("[data-required-key]:checked") !== null
    )
  }

  newSectionId() {
    return `s${crypto.randomUUID().slice(0, 8)}`
  }
}
